import { type FC, useEffect, useState } from 'react';

import type { CmsContentTypes } from '@eneco/cms-types/generated';
import AddressFinder, {
  defaultValues as addressFinderDefaultValues,
  addressFinderSchema,
} from '@eneco/components/AddressFinder/AddressFinder';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import { ApiError } from '@eneco/dc/client/ApiError';
import { typedYupResolver } from '@eneco/hooks/form';
import { NotificationBox, Stack } from '@eneco/sparky';
import { Customers_Address_AddressModel } from '@monorepo-types/dc';
import { useMachine } from '@xstate/react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { Header, Layout, NextButton } from '../../components';
import { useStepContent } from '../../hooks';
import { addressAvailabilityMachine } from '../../machines/addressAvailabilityMachine';
import { FlowContext } from '../../types';
import { useFlowHooks } from '../../utils/FlowProvider';
import { readSectionField } from '../../utils/readSectionField';

interface FormFields {
  postalCode: string;
  houseNumber: number;
  houseNumberSuffix?: string;
  city: string;
  street: string;
}

/**
 * The names the step is authored under: each lead flow keeps its own address step, and their generated
 * content types carry the same fields, so the step reads all three the same way.
 */
export type AddressFinderAvailabilityContentName =
  'CvKetelKoopFlowAddressStep' | 'HeatPumpLeadFlowAddressStep' | 'SolarBoilerLeadFlowAddressStep';

type UnavailableNotification = CmsContentTypes[AddressFinderAvailabilityContentName]['notification'];

interface Props {
  /** The name the flow's address step is authored under. */
  contentName: AddressFinderAvailabilityContentName;
  product: string;
  orderType: string;
  /** When true, unavailable addresses use TrackedNotificationBox instead of NotificationBox. */
  trackUnavailableNotification?: boolean;
}

export const AddressFinderAvailabilityStep: FC<Props> = ({
  contentName,
  product,
  orderType,
  trackUnavailableNotification = false,
}) => {
  const content = useStepContent(contentName);
  // Sitecore keeps this notification in the step's `content` section, beside a section called `notification`.
  const unavailableNotification = readSectionField<UnavailableNotification>(content, 'content', 'notification');
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContext>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const [isSuffixRequired, setIsSuffixRequired] = useState(false);
  const [errorData, setErrorData] = useState<ApiError>();
  const [isLoading, setIsLoading] = useState(false);
  const [addressFinderData, setAddressFinderData] = useState<Customers_Address_AddressModel>();

  const postalCode = useFlowSelector(state => state.context.postalCode);
  const houseNumber = useFlowSelector(state => state.context.houseNumber);
  const houseNumberSuffix = useFlowSelector(state => state.context.houseNumberSuffix);
  const street = useFlowSelector(state => state.context.street);
  const city = useFlowSelector(state => state.context.city);

  const [availabilityState, sendToAvailabilityMachine] = useMachine(addressAvailabilityMachine);

  const formSchema = yup.object({
    ...addressFinderSchema(isSuffixRequired).fields,
  });

  const formMethods = useForm<FormFields>({
    mode: 'onBlur',
    defaultValues: {
      ...addressFinderDefaultValues,
      postalCode,
      houseNumber,
      houseNumberSuffix: houseNumberSuffix ?? undefined,
      street,
      city,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const {
    handleSubmit,
    getValues,
    formState: { isSubmitSuccessful },
  } = formMethods;

  useEffect(() => {
    if (addressFinderData?.postalCode && addressFinderData?.houseNumber) {
      sendToAvailabilityMachine({
        type: 'CHECK_ADDRESS',
        value: addressFinderData.postalCode.substring(0, 2),
        product,
        orderType,
      });
    }
  }, [addressFinderData?.postalCode, addressFinderData?.houseNumber, orderType, product, sendToAvailabilityMachine]);

  useEffect(() => {
    if (isSubmitSuccessful && !errorData && addressFinderData && availabilityState.context?.isAvailable) {
      const formData = getValues();

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          ...addressFinderData,
          ...formData,
          postalCode: addressFinderData?.postalCode?.toString() ?? formData.postalCode.replace(/\s+/g, ''),
        },
      });
    }
  }, [
    availabilityState.context?.isAvailable,
    isSubmitSuccessful,
    addressFinderData,
    errorData,
    getValues,
    sendToFlowMachine,
  ]);

  const renderButtonAndAvailabilityNotification = () => {
    if (availabilityState.context?.isAvailable === false) {
      const notificationProps = {
        isAlert: false,
        variant: unavailableNotification?.variant,
        title: unavailableNotification?.title ?? '',
        text: <RichText html={unavailableNotification?.content} />,
      };

      return trackUnavailableNotification ? (
        <TrackedNotificationBox {...notificationProps} />
      ) : (
        <NotificationBox {...notificationProps} />
      );
    }

    return <NextButton isLoading={isSubmitSuccessful || isLoading}>{content?.nextStepText}</NextButton>;
  };

  return (
    <FormProvider {...formMethods}>
      <Layout.Content variant="A" handleSubmit={handleSubmit}>
        <Header>
          <Header.Title>{content?.title}</Header.Title>
          {content?.text && <Header.Text>{content.text}</Header.Text>}
        </Header>

        <Stack.Item as="fieldset">
          <AddressFinder
            isSuffixRequired={isSuffixRequired}
            setIsSuffixRequired={setIsSuffixRequired}
            customLabels={{
              houseNumber: content?.houseNumberFormField,
              houseNumberSuffix: content?.houseNumberSuffixFormField,
              postalCode: content?.postalCodeFormField,
            }}
            setErrorData={setErrorData}
            setIsLoading={setIsLoading}
            setAddressFinderData={setAddressFinderData}
          />
        </Stack.Item>
        {renderButtonAndAvailabilityNotification()}
      </Layout.Content>
    </FormProvider>
  );
};
